import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  CheckCircle2, CalendarDays, Clock, Loader2,
  ChevronLeft, ChevronRight, Smartphone, X
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
// Créneaux client : horaires d'ouverture uniquement (9h–19h, 30 min)
const generateClientSlots = () => {
  const slots = [];
  for (let h = 9; h < 19; h++) {
    slots.push({ value: `${String(h).padStart(2,"0")}:00`, label: `${String(h).padStart(2,"0")}:00` });
    slots.push({ value: `${String(h).padStart(2,"0")}:30`, label: `${String(h).padStart(2,"0")}:30` });
  }
  return slots;
};
const CLIENT_SLOTS = generateClientSlots();
// Compat affichage legacy (morning/afternoon/evening)
const legacyLabel = (v) => ({"morning":"Matin (9h–12h)","afternoon":"Après-midi (14h–17h)","evening":"Fin de journée (17h–19h)"}[v] || v || "—");;

export default function IntakeProposalPage() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialAction = searchParams.get("action"); // "accept" | "counter"

  const [proposal, setProposal] = useState(null);
  const [shopName, setShopName] = useState("AMCPhone Martinique");
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(null); // null | "accept_confirm" | "counter" | "done_accepted" | "done_counter"
  const [submitting, setSubmitting] = useState(false);

  // Counter-propose form
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [counterMessage, setCounterMessage] = useState("");

  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/shop-info`)
      .then(r => r.json()).then(d => { if (d.shop_name) setShopName(d.shop_name); }).catch(() => {});
  }, []);

useEffect(() => {
    fetchProposal();
  }, []);

  useEffect(() => {
    if (proposal && initialAction) {
      if (initialAction === "accept") setStep("accept_confirm");
      else if (initialAction === "counter") setStep("counter");
    }
  }, [proposal, initialAction]);

  const fetchProposal = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/intake-request/${requestId}/proposal`);
      setProposal(res.data);
    } catch (err) {
      toast.error("Demande introuvable");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/intake-request/${requestId}/accept-proposal`);
      setStep("done_accepted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la confirmation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCounter = async () => {
    if (!selectedDate) { toast.error("Veuillez sélectionner une date"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/intake-request/${requestId}/counter-propose`, null, {
        params: {
          counter_date: selectedDate,
          counter_time_slot: selectedTime,
          counter_message: counterMessage
        }
      });
      setStep("done_counter");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      daysInMonth: new Date(year, month + 1, 0).getDate(),
      startingDay: new Date(year, month, 1).getDay()
    };
  };

  const isSelectable = (date) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return date >= today && date.getDay() !== 0 && date.getDay() !== 6;
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth()+1).padStart(2,"0");
    const d = String(date.getDate()).padStart(2,"0");
    return `${y}-${m}-${d}`;
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) return "";
    const [y,m,d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const cells = [];
    for (let i = 0; i < startingDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth()-1, 1))} className="p-2 hover:bg-secondary rounded-lg">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-semibold text-sm">{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
          <button onClick={() => setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth()+1, 1))} className="p-2 hover:bg-secondary rounded-lg">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map(d => <div key={d} className="text-center text-xs text-gray-500 font-medium py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), d);
            const selectable = isSelectable(date);
            const dateStr = formatDate(date);
            const isSelected = selectedDate === dateStr;
            return (
              <button
                key={i}
                onClick={() => { if (selectable) { setSelectedDate(dateStr); setSelectedTime(null); } }}
                disabled={!selectable}
                className={`w-full aspect-square rounded-lg text-sm font-medium transition-all ${
                  isSelected ? "bg-primary text-white shadow-md" :
                  selectable ? "hover:bg-primary/20 text-gray-900" :
                  "text-gray-500/30 cursor-not-allowed"
                }`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!proposal) return (
    <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-4">
      <div className="text-center">
        <X className="w-12 h-12 text-destructive mx-auto mb-3" />
        <p className="text-gray-900 font-semibold">Demande introuvable</p>
        <p className="text-gray-500 text-sm mt-1">Ce lien est invalide ou expiré.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <Smartphone className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-bold text-xl text-gray-900">{shopName}</h1>
          <p className="text-gray-500 text-sm mt-1">Gestion de votre rendez-vous</p>
        </div>

        {/* Infos réparation */}
        <Card className="bg-gray-50 border-gray-200 mb-4">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 mb-1">Votre demande</p>
            <p className="font-semibold text-gray-900">{proposal.client_name}</p>
            <p className="text-sm text-gray-500">{proposal.device_brand} {proposal.device_model}</p>
          </CardContent>
        </Card>

        {/* ─── ÉTAPE : Choix initial ─── */}
        {!step && proposal.status === "date_proposed" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-violet-500/10 border border-violet-500/30">
              <p className="text-sm font-medium text-violet-300 mb-2">Nous vous proposons :</p>
              <p className="text-2xl font-bold text-gray-900">{formatDateFr(proposal.proposed_date)}</p>
              <p className="text-sm text-gray-500 mt-1">{legacyLabel(proposal.proposed_time_slot)}</p>
              {proposal.proposed_message && (
                <p className="text-xs text-violet-300 mt-2 italic">"{proposal.proposed_message}"</p>
              )}
            </div>
            <Button onClick={() => setStep("accept_confirm")} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12">
              <CheckCircle2 className="w-5 h-5 mr-2" /> Accepter cette date
            </Button>
            <Button onClick={() => setStep("counter")} variant="outline" className="w-full h-12 border-amber-500 text-amber-500 hover:bg-amber-500/10">
              <CalendarDays className="w-5 h-5 mr-2" /> Proposer une autre date
            </Button>
          </div>
        )}

        {/* ─── ÉTAPE : Confirmation acceptation ─── */}
        {step === "accept_confirm" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <p className="text-sm font-medium text-emerald-400 mb-2">Vous allez confirmer le rendez-vous :</p>
              <p className="text-2xl font-bold text-gray-900">{formatDateFr(proposal.proposed_date)}</p>
              <p className="text-sm text-gray-500 mt-1">{legacyLabel(proposal.proposed_time_slot)}</p>
            </div>
            <Button onClick={handleAccept} disabled={submitting} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
              Confirmer ce rendez-vous
            </Button>
            <Button onClick={() => setStep(null)} variant="outline" className="w-full" disabled={submitting}>
              Retour
            </Button>
          </div>
        )}

        {/* ─── ÉTAPE : Contre-proposition ─── */}
        {step === "counter" && (
          <div className="space-y-5">
            <p className="text-sm text-gray-500 text-center">
              Choisissez la date qui vous convient :
            </p>

            {/* Calendrier */}
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                {renderCalendar()}
              </CardContent>
            </Card>

            {/* Créneau */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Clock className="w-4 h-4 text-gray-500" /> Créneau souhaité
                {selectedDate && !selectedTime && (
                  <span className="text-xs text-amber-500 font-medium ml-1">← Choisissez un créneau</span>
                )}
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {CLIENT_SLOTS.map(slot => (
                  <button
                    key={slot.value}
                    type="button"
                    onClick={() => setSelectedTime(slot.value)}
                    className={`py-2 px-1 rounded-lg text-sm font-medium transition-all border ${
                      selectedTime === slot.value
                        ? "bg-primary text-white border-primary"
                        : "bg-gray-100 text-gray-600 border-gray-200 hover:border-primary/40"
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message optionnel */}
            <div className="space-y-2">
              <Label className="text-sm">Message (optionnel)</Label>
              <Textarea
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                placeholder="Précisez si vous avez une contrainte particulière..."
                className="bg-gray-100 text-sm"
                rows={2}
              />
            </div>

            {selectedDate && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm text-center">
                <span className="text-gray-500">Date sélectionnée : </span>
                <span className="font-bold text-primary">{formatDateFr(selectedDate)}</span>
                <span className="text-gray-500"> — {selectedTime}</span>
              </div>
            )}

            <Button onClick={handleCounter} disabled={submitting || !selectedDate || !selectedTime} className="w-full bg-amber-500 hover:bg-amber-600 text-white h-12">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarDays className="w-5 h-5 mr-2" />}
              Envoyer ma proposition
            </Button>
            <Button onClick={() => setStep(null)} variant="outline" className="w-full" disabled={submitting}>
              Retour
            </Button>
          </div>
        )}

        {/* ─── ÉTAPE : Succès acceptation ─── */}
        {step === "done_accepted" && (
          <div className="text-center space-y-4 py-8">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Rendez-vous confirmé !</h2>
            <p className="text-gray-500 text-sm">
              Votre rendez-vous du <strong>{formatDateFr(proposal.proposed_date)}</strong> est bien enregistré.<br />
              Vous recevrez un email de confirmation.
            </p>
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
              <p className="font-bold text-gray-900 text-lg">{formatDateFr(proposal.proposed_date)}</p>
              <p className="text-sm text-gray-500">{legacyLabel(proposal.proposed_time_slot)}</p>
            </div>
          </div>
        )}

        {/* ─── ÉTAPE : Succès contre-proposition ─── */}
        {step === "done_counter" && (
          <div className="text-center space-y-4 py-8">
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto">
              <CalendarDays className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Proposition envoyée !</h2>
            <p className="text-gray-500 text-sm">
              Notre équipe va étudier votre demande et vous recontacter rapidement.
            </p>
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <p className="font-bold text-gray-900 text-lg">{formatDateFr(selectedDate)}</p>
              <p className="text-sm text-gray-500">{selectedTime}</p>
            </div>
          </div>
        )}

        {/* Si déjà traité */}
        {!step && proposal.status === "confirmed" && (
          <div className="text-center space-y-3 py-8">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-semibold text-gray-900">Rendez-vous déjà confirmé</p>
            <p className="text-gray-500 text-sm">
              Votre rendez-vous du <strong>{formatDateFr(proposal.original_date)}</strong> est confirmé.
            </p>
          </div>
        )}

        {!step && proposal.status === "date_counter_proposed" && (
          <div className="text-center space-y-3 py-8">
            <CalendarDays className="w-12 h-12 text-amber-500 mx-auto" />
            <p className="font-semibold text-gray-900">Contre-proposition en cours</p>
            <p className="text-gray-500 text-sm">
              Notre équipe va vous recontacter pour valider votre date.
            </p>
          </div>
        )}

        {/* Fallback — statut non géré (cancelled, pending, converted...) */}
        {!step && !["date_proposed","confirmed","date_counter_proposed"].includes(proposal.status) && (
          <div className="text-center space-y-3 py-8">
            <Smartphone className="w-12 h-12 text-gray-500 mx-auto" />
            <p className="font-semibold text-gray-900">Demande en cours de traitement</p>
            <p className="text-gray-500 text-sm">
              Notre équipe prend en charge votre demande et vous contactera très bientôt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
